import { Spinner } from '@app/components/icons/spinner';
import { LazyRoute } from '@app/components/lazy-route';
import { PusherProviderWrapper } from '@app/components/pusher-provider';
import {
  FLAG_CONTENT_HUB_ENABLE_RECORDING_LAB,
  FLAG_CONTENT_HUB_ENABLE_UPGRADE_BANNER,
} from '@app/feature-flags/flags';
import { useFlags } from '@app/hooks/use-flags';
import { selectContentHubConfig } from '@app/routes/content/selectors';
import { selectIsAiDisabled } from '@app/routes/organization/selectors';
import { useAppSelector } from '@app/store';
import { FLAG_POST_EVENT_ENABLE_CREATOR_LAB_CONSOLIDATION } from '@app/utilities/flags';
import React, { lazy, Suspense } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';

import { UpgradeBanner } from './upgrade-banner';
import { isValidContentTypeSlug } from './utils';

const ComingSoon = lazy(() =>
  import('./coming-soon').then(module => ({ default: module.ComingSoon })),
);
const LabPage = lazy(() => import('./lab-page'));

const Lab = () => {
  const flags = useFlags();
  const isLabEnabled = flags?.[FLAG_CONTENT_HUB_ENABLE_RECORDING_LAB];
  const isConsolidationEnabled =
    flags?.[FLAG_POST_EVENT_ENABLE_CREATOR_LAB_CONSOLIDATION];
  const { tabType } = useParams<{ tabType?: string }>();
  const location = useLocation();

  const isUpgradeBannerEnabled =
    flags?.[FLAG_CONTENT_HUB_ENABLE_UPGRADE_BANNER];
  const config = useAppSelector(selectContentHubConfig);
  const isContentHubConfigLoaded = config.status === 'success';
  const hasContentHubCreatorLabAccess =
    config.hasContentHubCreatorLabAccess ?? false;
  const isAiDisabled = useAppSelector(selectIsAiDisabled);

  // Guard 1: AI disabled for organization - redirect to content library
  if (isAiDisabled) {
    return <Navigate to="../library" replace />;
  }

  // Guard 2: Redirect to editor with content-composer tab when consolidation flag is enabled
  if (isConsolidationEnabled) {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('editor-tab', 'content-composer');
    const redirectPath = tabType
      ? `../../editor?${searchParams.toString()}`
      : `../editor?${searchParams.toString()}`;
    return <Navigate to={redirectPath} replace />;
  }

  // Guard 3: Redirect from /lab to /lab/social-post
  if (!tabType) {
    return <Navigate to="social-post" replace />;
  }

  // Guard 4: Redirect invalid tab types to default
  if (!isValidContentTypeSlug(tabType)) {
    return <Navigate to="../social-post" replace />;
  }

  if (!isLabEnabled) {
    return (
      <Suspense fallback={<Spinner size="medium" />}>
        <ComingSoon />
      </Suspense>
    );
  }
  if (
    isUpgradeBannerEnabled &&
    isContentHubConfigLoaded &&
    !hasContentHubCreatorLabAccess
  ) {
    return <UpgradeBanner />;
  }
  return (
    <LazyRoute>
      <PusherProviderWrapper>
        <LabPage />
      </PusherProviderWrapper>
    </LazyRoute>
  );
};

export { Lab };
